import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UploadSimple, Sparkle, Play, Pause, FilmSlate, DownloadSimple, Broadcast } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { uploadMedia } from "../lib/uploadMedia";

/**
 * AI Voiceover — upload a video, write a script, pick an AI voice, generate
 * narration, preview it over the video, render the mix (backend ffmpeg) and publish.
 */
export default function AIVoiceover() {
  const nav = useNavigate();
  const [voices, setVoices] = useState([]);
  const [meta, setMeta] = useState(null);
  const [file, setFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [script, setScript] = useState("");
  const [voiceId, setVoiceId] = useState("EXAVITQu4vr4xnSDxMaL");
  const [narration, setNarration] = useState(null);
  const [origVol, setOrigVol] = useState(0.25);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState("");
  const [rendered, setRendered] = useState(null); // { blob, url }
  const [caption, setCaption] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/ai/voice/voices").then((r) => { setVoices(r.data.voices); setMeta(r.data); }).catch(() => {});
    return () => { audioRef.current?.pause(); };
  }, []);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("video")) return toast.error("Pick a video file");
    if (f.size > 90 * 1024 * 1024) return toast.error("Video must be under 90 MB");
    setFile(f);
    setVideoUrl(URL.createObjectURL(f));
    setRendered(null);
  };

  const generate = async () => {
    if (!script.trim()) return;
    setBusy("narration");
    try {
      const { data } = await api.post("/ai/voice/tts", { text: script, voice_id: voiceId, kind: "voiceover", title: `Voiceover · ${file?.name || "clip"}` });
      setNarration(data);
      setRendered(null);
      toast.success("Narration ready — preview it over your video");
    } catch (e) { toast.error(e?.response?.data?.detail || "Narration failed"); }
    finally { setBusy(""); }
  };

  const togglePreview = () => {
    const v = videoRef.current;
    if (!v || !narration) return;
    if (previewing) {
      v.pause(); audioRef.current?.pause(); setPreviewing(false); return;
    }
    v.currentTime = 0; v.volume = origVol; v.play().catch(() => {});
    const a = new Audio(fileUrl(narration.url));
    audioRef.current = a;
    setTimeout(() => a.play().catch(() => {}), offset * 1000);
    a.onended = () => { setPreviewing(false); };
    setPreviewing(true);
  };

  const render = async () => {
    if (!file || !narration) return;
    setBusy("render");
    try {
      const fd = new FormData();
      fd.append("video", file);
      fd.append("audio_id", narration.id);
      fd.append("original_volume", String(origVol));
      fd.append("narration_volume", "1.0");
      fd.append("start_offset", String(offset));
      const res = await api.post("/ai/voice/voiceover/render", fd, { responseType: "blob", headers: { "Content-Type": "multipart/form-data" } });
      const blob = res.data;
      if (rendered?.url) URL.revokeObjectURL(rendered.url);
      setRendered({ blob, url: URL.createObjectURL(blob) });
      toast.success("Rendered! Preview below, then publish.");
    } catch (e) {
      let msg = e?.response?.data?.detail || e.message || "Render failed";
      if (e?.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* ignore */ } }
      toast.error(msg);
    } finally { setBusy(""); }
  };

  const publish = async () => {
    if (!rendered) return;
    setBusy("publish");
    try {
      const out = new File([rendered.blob], `voiceover-${Date.now()}.mp4`, { type: "video/mp4" });
      const uploaded = await uploadMedia(out, {});
      const body = { caption: caption || script.slice(0, 120), media_path: uploaded.path, media_type: uploaded.media_type, privacy: "public", ai_labels: ["ai_voice"] };
      if (uploaded.mux) { body.mux_upload_id = uploaded.mux.upload_id; body.mux_passthrough = uploaded.mux.passthrough; body.mux_status = uploaded.mux.status; }
      await api.post("/posts", body);
      toast.success("Published with AI voice label ✦");
      nav("/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(""); }
  };

  const download = () => {
    if (!rendered) return;
    const a = document.createElement("a");
    a.href = rendered.url; a.download = "giffwurldaunce-voiceover.mp4"; a.click();
  };

  const maxChars = meta?.limits?.max_chars_per_request || 500;

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-6" data-testid="ai-voiceover">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream" data-testid="voiceover-back"><ArrowLeft size={18} /></button>
        <div>
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Creator Studio · AI</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">AI Voiceover</h1>
        </div>
      </div>
      {meta && <p className="text-cream/40 text-[11px] mt-2">{meta.usage.voiceovers_today}/{meta.limits.voiceovers_per_day} renders used today</p>}

      {/* Step 1 — video */}
      <Step n="1" title="Upload your video">
        {!videoUrl ? (
          <label data-testid="voiceover-pick" className="flex flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-white/15 bg-white/5 p-8 cursor-pointer hover:border-papaya/50 transition-colors">
            <UploadSimple size={28} className="text-papaya" weight="bold" />
            <span className="text-cream text-sm font-bold">Tap to choose a video</span>
            <span className="text-cream/40 text-[11px]">MP4 / MOV / WEBM · up to 90 MB</span>
            <input type="file" accept="video/*" className="hidden" onChange={pick} data-testid="voiceover-file" />
          </label>
        ) : (
          <div className="rounded-3xl overflow-hidden bg-black relative">
            <video ref={videoRef} src={rendered?.url || videoUrl} controls={!!rendered} playsInline className="w-full max-h-[50vh] object-contain" data-testid="voiceover-video" />
            {rendered && <span className="absolute top-3 left-3 crystal-glass text-cream text-[9px] font-black tracking-[0.2em] uppercase px-2 py-1 rounded-full">✦ Rendered with AI voice</span>}
            <label className="absolute top-3 right-3 crystal-glass text-cream text-[10px] font-black tracking-[0.2em] uppercase px-3 py-1.5 rounded-full cursor-pointer">
              Change<input type="file" accept="video/*" className="hidden" onChange={pick} />
            </label>
          </div>
        )}
      </Step>

      {/* Step 2 — script + voice */}
      <Step n="2" title="Write the narration">
        <textarea
          value={script}
          onChange={(e) => setScript(e.target.value.slice(0, maxChars))}
          rows={3}
          placeholder="Today I'm going to show you three simple dance moves…"
          data-testid="voiceover-script"
          className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya text-sm"
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-cream/40 text-[11px]">{script.length}/{maxChars}</span>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 mt-2" data-testid="voiceover-voices">
          {voices.map((v) => {
            const locked = v.is_premium && !meta?.is_premium_user;
            return (
              <button
                key={v.voice_id}
                disabled={locked}
                onClick={() => setVoiceId(v.voice_id)}
                data-testid={`voiceover-voice-${v.voice_id}`}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${voiceId === v.voice_id ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"} disabled:opacity-40`}
              >
                {v.name}{v.is_premium ? " ★" : ""}
              </button>
            );
          })}
        </div>
        <button onClick={generate} disabled={!script.trim() || busy !== ""} data-testid="voiceover-generate" className="mt-3 w-full py-3.5 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-xs flex items-center justify-center gap-2 disabled:opacity-40">
          <Sparkle size={16} weight="fill" /> {busy === "narration" ? "Generating…" : narration ? "Regenerate narration" : "Generate narration"}
        </button>
        {narration && (
          <div className="mt-3 flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3" data-testid="voiceover-narration">
            <audio src={fileUrl(narration.url)} controls className="flex-1 h-9" />
            <span className="text-cream/60 text-xs">{narration.voice_name}</span>
          </div>
        )}
      </Step>

      {/* Step 3 — mix + render */}
      <Step n="3" title="Mix & render">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Original audio {Math.round(origVol * 100)}%</span>
            <input type="range" min="0" max="1" step="0.05" value={origVol} onChange={(e) => setOrigVol(parseFloat(e.target.value))} className="w-full accent-[#C66A4A]" data-testid="voiceover-origvol" />
          </label>
          <label className="block">
            <span className="text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Start at {offset.toFixed(1)}s</span>
            <input type="range" min="0" max="30" step="0.5" value={offset} onChange={(e) => setOffset(parseFloat(e.target.value))} className="w-full accent-[#C66A4A]" data-testid="voiceover-offset" />
          </label>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={togglePreview} disabled={!narration || !videoUrl} data-testid="voiceover-preview" className="flex-1 py-3 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5 disabled:opacity-40">
            {previewing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />} {previewing ? "Stop preview" : "Preview mix"}
          </button>
          <button onClick={render} disabled={!narration || !file || busy !== ""} data-testid="voiceover-render" className="flex-1 py-3 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5 disabled:opacity-40">
            <FilmSlate size={14} weight="fill" /> {busy === "render" ? "Rendering…" : "Render video"}
          </button>
        </div>
      </Step>

      {rendered && (
        <Step n="4" title="Publish">
          <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption for your post" data-testid="voiceover-caption" className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya text-sm" />
          <p className="text-cream/40 text-[11px] mt-2">Posts made here carry an “AI voice” label so viewers know narration is AI-generated.</p>
          <div className="flex gap-2 mt-3">
            <button onClick={download} data-testid="voiceover-download" className="flex-1 py-3 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5"><DownloadSimple size={14} weight="bold" /> Download</button>
            <button onClick={publish} disabled={busy !== ""} data-testid="voiceover-publish" className="flex-1 py-3 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5 disabled:opacity-40"><Broadcast size={14} weight="fill" /> {busy === "publish" ? "Publishing…" : "Publish"}</button>
          </div>
        </Step>
      )}
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <section className="mt-7">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-6 h-6 rounded-full bg-papaya text-ink text-[11px] font-black flex items-center justify-center">{n}</span>
        <h2 className="text-cream font-black text-base">{title}</h2>
      </div>
      {children}
    </section>
  );
}
